// Bulk CSV menu import modal. Two screens in one overlay:
//   1. Start  - "Download template" + "Upload CSV" (file picker)
//   2. Preview - the assembled items (grouped from the CSV rows) with flags,
//                so the owner sees exactly what will be saved before committing.
//
// Self-contained: builds its own overlay on first open and appends it to <body>.
// Uses the shared .modal-overlay-form + .modal-close so click-outside-to-close
// (modalBackdrop.js) works for free. All free text rendered through esc().

import { esc } from "../escape.js";
import { formatMoney } from "../currency.js";
import { parseCsv, detectColumns, buildItems, downloadTemplate } from "../csvImport.js";

class MenuImportView {
  _overlay = null;
  _onConfirm = null; // controller callback: (items, newCategories) => Promise
  _ctx = null; // { existingNames:Set, existingCategories:Set } from current catalogue
  _parsed = null; // last buildItems() result

  // Open the modal on its start screen. ctx carries the current catalogue so the
  // preview can flag duplicates + new categories.
  open(ctx, onConfirm) {
    this._ctx = ctx;
    this._onConfirm = onConfirm;
    this._ensureOverlay();
    this._showStart();
    this._overlay.classList.remove("hidden");
  }

  close() {
    this._overlay?.classList.add("hidden");
    this._parsed = null;
  }

  _ensureOverlay() {
    if (this._overlay) return;
    const el = document.createElement("div");
    el.className = "modal-overlay-form menu-import-overlay hidden";
    el.id = "menuImportOverlay";
    el.innerHTML = `
      <div class="menu-import-modal modal-content">
        <div class="menu-import-header">
          <h2 class="menu-import-title">Import items from CSV</h2>
          <button class="modal-close" aria-label="Close">&times;</button>
        </div>
        <div class="menu-import-body"></div>
      </div>`;
    document.body.appendChild(el);
    this._overlay = el;

    el.querySelector(".modal-close").addEventListener("click", () => this.close());

    // One delegated click handler for every button across both screens.
    el.addEventListener("click", (e) => {
      if (e.target.closest("#menuImportTemplateBtn")) {
        downloadTemplate();
      } else if (e.target.closest("#menuImportPickBtn")) {
        el.querySelector("#menuImportFileInput")?.click();
      } else if (e.target.closest("#menuImportBackBtn")) {
        this._showStart();
      } else if (e.target.closest("#menuImportConfirmBtn")) {
        this._confirm();
      }
    });

    el.addEventListener("change", (e) => {
      if (e.target.id === "menuImportFileInput") this._onFile(e.target.files?.[0]);
    });
  }

  _body() {
    return this._overlay.querySelector(".menu-import-body");
  }

  _showStart() {
    this._parsed = null;
    this._body().innerHTML = `
      <div class="menu-import-start">
        <p class="menu-import-intro">
          Add all your items at once from a spreadsheet - the fastest way to get set up.
        </p>
        <ol class="menu-import-steps">
          <li>
            <span class="menu-import-step-num">1</span>
            <div class="menu-import-step-text">
              <b>Download the template</b>
              <span>A ready-made spreadsheet with example rows already filled in.</span>
            </div>
          </li>
          <li>
            <span class="menu-import-step-num">2</span>
            <div class="menu-import-step-text">
              <b>Add your items</b>
              <span>Open it in Excel or Google Sheets and replace the examples with your own.</span>
            </div>
          </li>
          <li>
            <span class="menu-import-step-num">3</span>
            <div class="menu-import-step-text">
              <b>Upload it back here</b>
              <span>You'll get a preview to check before anything is saved.</span>
            </div>
          </li>
        </ol>
        <details class="menu-import-help">
          <summary>How the columns work</summary>
          <ul>
            <li><b>Simple item:</b> fill <b>name</b>, <b>price</b> and <b>category</b>, and leave the option columns blank.</li>
            <li><b>Item with choices</b> (e.g. sizes): add one row per choice and repeat the item's name on each row.</li>
            <li>Items whose name already exists are skipped, and new categories are created automatically.</li>
          </ul>
        </details>
        <div class="menu-import-start-actions">
          <button class="btn" id="menuImportTemplateBtn" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download template
          </button>
          <button class="btn primary" id="menuImportPickBtn" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload CSV
          </button>
        </div>
        <input type="file" accept=".csv,text/csv" id="menuImportFileInput" hidden />
      </div>`;
  }

  async _onFile(file) {
    if (!file) return;
    let text = "";
    try {
      text = await file.text();
    } catch {
      this._showError("Could not read that file. Please try again.");
      return;
    }
    const rows = parseCsv(text);
    if (rows.length < 2) {
      this._showError("That file looks empty. Use the template as a starting point.");
      return;
    }
    const [headers, ...dataRows] = rows;
    const mapping = detectColumns(headers);
    if (mapping.name < 0) {
      this._showError("Couldn't find a <b>name</b> column. Check the header row matches the template.");
      return;
    }
    this._parsed = buildItems(dataRows, mapping, this._ctx);
    if (!this._parsed.items.length) {
      this._showError("No items found in that file. Check the header row matches the template.");
      return;
    }
    this._showPreview();
    // allow re-picking the same file later
    this._overlay.querySelector("#menuImportFileInput")?.remove();
  }

  _showError(msg) {
    const start = this._body().querySelector(".menu-import-start");
    if (!start) { this._showStart(); }
    let note = this._body().querySelector(".menu-import-error");
    if (!note) {
      note = document.createElement("p");
      note.className = "menu-import-error";
      this._body().querySelector(".menu-import-start")?.prepend(note);
    }
    note.innerHTML = msg; // msg is app-controlled copy, not user input
    // reset the file input so the same file can be retried
    const input = this._overlay.querySelector("#menuImportFileInput");
    if (input) input.value = "";
  }

  _showPreview() {
    const { items, summary } = this._parsed;

    const rowsHtml = items
      .map((it) => {
        const variantText = it.hasVariants
          ? it.variants
              .map((g) => `${esc(g.optionLabel)} (${g.options.length})`)
              .join(", ")
          : "<span class=\"menu-import-muted\">-</span>";
        const status = it.duplicate
          ? `<span class="menu-import-badge menu-import-badge--skip">Skipped - exists</span>`
          : `<span class="menu-import-badge menu-import-badge--ok">Add</span>`;
        const catBadge = it.newCategory && !it.duplicate
          ? ` <span class="menu-import-badge menu-import-badge--new">new</span>`
          : "";
        const priceBadge = it.priceConflict
          ? ` <span class="menu-import-badge menu-import-badge--warn">${it.priceConflictCount} different prices</span>`
          : "";
        return `
          <tr class="${it.duplicate ? "menu-import-row--skip" : ""}">
            <td>${status}</td>
            <td>${esc(it.itemName)}</td>
            <td>${it.hasVariants ? "<span class=\"menu-import-muted\">varies</span>" : esc(formatMoney(it.price))}${priceBadge}</td>
            <td>${esc(it.category || "uncategorized")}${catBadge}</td>
            <td>${variantText}</td>
          </tr>`;
      })
      .join("");

    const newCats = summary.newCategories.length
      ? ` &middot; ${summary.newCategories.length} new categor${summary.newCategories.length === 1 ? "y" : "ies"}`
      : "";
    const skipped = summary.duplicates
      ? ` &middot; ${summary.duplicates} skipped`
      : "";
    const badRows = summary.invalidVariantRows
      ? ` &middot; ${summary.invalidVariantRows} incomplete row${summary.invalidVariantRows === 1 ? "" : "s"} ignored`
      : "";
    const priceConflictNote = summary.priceConflicts
      ? ` &middot; ${summary.priceConflicts} item${summary.priceConflicts === 1 ? " has" : "s have"} mismatched prices`
      : "";

    const conflictWarning = summary.priceConflicts
      ? `<div class="menu-import-warning">
          <b>Heads up - some items were entered with more than one price:</b>
          <ul>
            ${items
              .filter((it) => it.priceConflict && !it.duplicate)
              .map(
                (it) =>
                  `<li><b>${esc(it.itemName)}</b> has ${it.priceConflictCount} entries with different prices - using ${esc(formatMoney(it.price))} (the first one) as its price. You can edit it after importing.</li>`
              )
              .join("")}
          </ul>
        </div>`
      : "";

    this._body().innerHTML = `
      <div class="menu-import-preview">
        <p class="menu-import-summary">
          <b>${summary.willImport}</b> item${summary.willImport === 1 ? "" : "s"} ready to import${newCats}${skipped}${badRows}${priceConflictNote}.
        </p>
        ${conflictWarning}
        <div class="menu-import-table-wrap">
          <table class="menu-import-table">
            <thead>
              <tr><th>Status</th><th>Name</th><th>Price</th><th>Category</th><th>Variants</th></tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
        <div class="menu-import-preview-actions">
          <button class="btn" id="menuImportBackBtn" type="button">Back</button>
          <button class="btn primary" id="menuImportConfirmBtn" type="button" ${summary.willImport ? "" : "disabled"}>
            Import ${summary.willImport} item${summary.willImport === 1 ? "" : "s"}
          </button>
        </div>
      </div>`;
  }

  async _confirm() {
    if (!this._parsed) return;
    const items = this._parsed.items.filter((i) => !i.duplicate);
    if (!items.length) return;
    const newCategories = this._parsed.summary.newCategories;

    const btn = this._overlay.querySelector("#menuImportConfirmBtn");
    if (btn) { btn.disabled = true; btn.textContent = "Importing…"; }

    try {
      await this._onConfirm?.(items, newCategories);
      this.close();
    } catch (err) {
      // surface failure but keep the preview so the owner can retry
      if (btn) {
        btn.disabled = false;
        btn.textContent = "Try again";
      }
      const note = document.createElement("p");
      note.className = "menu-import-error";
      note.textContent = err?.message ?? "Import failed. Please try again.";
      this._body().querySelector(".menu-import-preview")?.prepend(note);
    }
  }
}

export default new MenuImportView();
